import { z } from "zod";

/**
 * Cadastro de materiais do laboratório — a mesma linha serve catálogo, ficha técnica e estoque.
 *
 * Arquivo próprio (e não dentro de `prosthesis.ts`, já grande) porque a ficha técnica e o estoque
 * vão crescer em cima daqui.
 */

/** Quantidade e fator chegam como texto do formulário ("0,0011") e viram número com 6 casas — a
 *  precisão de `Decimal(14,6)` no banco. Vírgula é aceita: é o que o teclado brasileiro produz. */
export const quantidadeSchema = z.preprocess(
  (v) => (typeof v === "string" ? Number(v.replace(",", ".")) : v),
  z.number().positive("Informe um número maior que zero").max(1_000_000),
);

/** Fator de correção: 1 = sem perda, 1,1 = compra-se 10% a mais do que a peça consome. Abaixo de 1
 *  não existe (seria "gasto menos do que uso"). */
export const fatorDeCorrecaoSchema = z.preprocess(
  (v) => (typeof v === "string" ? Number(v.replace(",", ".")) : v),
  z.number().min(1, "O fator não pode ser menor que 1").max(100),
);

export const materialSubcategoryCreateSchema = z.object({
  label: z.string().trim().min(2, "Nome muito curto").max(60),
});
export type MaterialSubcategoryCreateInput = z.infer<typeof materialSubcategoryCreateSchema>;

export const materialSubcategoryUpdateSchema = materialSubcategoryCreateSchema.extend({
  id: z.string().min(1),
});

/** Campos de compra e custo, compartilhados por criar e editar. Todos opcionais: o laboratório
 *  cadastra o material primeiro e preenche preço quando souber. */
const materialLineDadosSchema = z.object({
  subcategoryId: z.string().min(1).nullish(),
  supplierPartnerId: z.string().min(1).nullish(),
  unit: z.string().trim().max(12).nullish(),
  purchasePriceCents: z.number().int().min(0).max(100_000_000).nullish(),
  defaultCorrectionFactor: fatorDeCorrecaoSchema.nullish(),
  /** Abaixo disto a sugestão de compra acusa. Zero é resposta válida ("não quero manter"). */
  minimumStock: z.preprocess(
    (v) => (typeof v === "string" ? (v.trim() === "" ? null : Number(v.replace(",", "."))) : v),
    z.number().min(0).max(1_000_000).nullish(),
  ),
  indirectConsumption: z.boolean().optional(),
  availableToDentist: z.boolean().optional(),
  /** Rótulos livres. Mesmos limites de `Patient.tags` — o componente de entrada é o mesmo. */
  tags: z.array(z.string().trim().min(1).max(50)).max(20).optional(),
});

export const materialLineCreateSchema = materialLineDadosSchema
  .extend({
    materialCategoryId: z.string().min(1),
    name: z.string().trim().min(2).max(160),
    manufacturerId: z.string().min(1).optional(),
    newManufacturerName: z.string().trim().min(2).max(120).optional(),
  })
  .refine((d) => !!d.manufacturerId !== !!d.newManufacturerName, {
    message: "Escolha um fabricante existente ou informe o nome de um novo, nunca os dois.",
  });
export type MaterialLineCreateInput = z.infer<typeof materialLineCreateSchema>;

/** Editar não troca fabricante nem categoria — isso mudaria a identidade do material e quebraria
 *  ficha técnica já montada em cima dele. Para trocar, cria-se outro e desativa-se este. */
export const materialLineUpdateSchema = materialLineDadosSchema.extend({
  id: z.string().min(1),
  name: z.string().trim().min(2).max(160),
});
export type MaterialLineUpdateInput = z.infer<typeof materialLineUpdateSchema>;
